import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from './api.js';
import { errMessage, makeT } from './i18n.js';
import { useLang } from './lang.js';
import Icon from './Icon.jsx';

/**
 * Panel asistanı — her ekranda sağ altta.
 *
 * İKİ KURAL:
 *  1. Paket kapalıysa ya da sunucuda anahtar yoksa düğme HİÇ çizilmez
 *     (`/api/ai/status`). Çalışmayan bir düğme göstermek, tıklayıp hata
 *     almaktan beterdir.
 *  2. Değişiklikler burada "önerilen değişiklik" kartı olarak görünür ve
 *     ancak kullanıcı onaylayınca uygulanır. Sunucu da aynı şeyi söyler;
 *     arayüz onu yalnızca görünür kılar.
 */
export default function Assistant() {
  const loc = useLocation();
  const lang = useLang();
  const t = makeT(lang);
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);       // {role, text}
  const [actions, setActions] = useState([]); // {id, tool, preview, status}
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [convId, setConvId] = useState(null);
  const endRef = useRef(null);

  useEffect(() => {
    api('/api/ai/status').then(setStatus).catch(() => setStatus({ enabled: false }));
  }, []);

  // Sipariş ekranında GÖRÜNMEZ: yüzen düğme «Gönder»in üstüne biniyordu ve
  // garson servis sırasında asistanla konuşmuyor (hız ekranı).
  const hidden = loc.pathname.startsWith('/order/');

  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [msgs, actions, open]);

  if (!status?.enabled || hidden) return null;

  async function send(e) {
    e?.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setErr('');
    setInput('');
    setMsgs((m) => [...m, { role: 'user', text }]);
    setBusy(true);
    try {
      const out = await api('/api/ai/chat', {
        method: 'POST',
        body: { message: text, conversationId: convId, locale: lang },
      });
      setConvId(out.conversationId);
      setMsgs((m) => [...m, { role: 'assistant', text: out.reply }]);
      if (out.actions?.length) setActions((a) => [...a, ...out.actions.map((x) => ({ ...x, status: 'PENDING' }))]);
      if (out.quota) setStatus((s) => ({ ...s, quota: out.quota }));
    } catch (e2) {
      setErr(errMessage(t, e2));
    } finally {
      setBusy(false);
    }
  }

  async function decide(action, apply) {
    setErr('');
    try {
      await api(`/api/ai/actions/${action.id}/${apply ? 'apply' : 'reject'}`, { method: 'POST' });
      setActions((as) => as.map((a) => (a.id === action.id ? { ...a, status: apply ? 'APPLIED' : 'REJECTED' } : a)));
    } catch (e2) {
      setErr(errMessage(t, e2));
    }
  }

  const q = status.quota;

  return (
    <>
      <button className="ai-fab" onClick={() => setOpen(!open)} aria-expanded={open} title={t('ai.title')}>
        <Icon n="sparkles" />
        <span className="ai-fab-label">{t('ai.title')}</span>
      </button>

      {open && (
        <div className="ai-panel" role="dialog" aria-label={t('ai.title')}>
          <header>
            <strong>{t('ai.title')}</strong>
            {q?.limit ? <span className="muted">{q.used}/{q.limit}</span> : <span />}
            <button className="ai-x" onClick={() => setOpen(false)} aria-label={t('close')}>×</button>
          </header>

          <div className="ai-body">
            {!msgs.length && <p className="muted ai-hint">{t('ai.empty')}</p>}
            {msgs.map((m, i) => (
              <p key={i} className={m.role === 'user' ? 'ai-msg me' : 'ai-msg'}>{m.text}</p>
            ))}
            {actions.map((a) => (
              <div key={a.id} className="ai-action">
                <p className="ai-action-h">{t('ai.proposed')}</p>
                <p className="ai-action-s">{a.preview?.summary || a.tool}</p>
                {a.status === 'PENDING' ? (
                  <div className="row">
                    <button className="primary" onClick={() => decide(a, true)}>
                      <Icon n="check" />{t('ai.apply')}
                    </button>
                    <button onClick={() => decide(a, false)}><Icon n="x" />{t('ai.reject')}</button>
                  </div>
                ) : (
                  <p className={a.status === 'APPLIED' ? 'ok' : 'muted'}>
                    {a.status === 'APPLIED' ? t('ai.applied') : t('ai.rejected')}
                  </p>
                )}
              </div>
            ))}
            {busy && <p className="muted ai-msg">{t('ai.thinking')}</p>}
            {err && <p className="err">{err}</p>}
            <div ref={endRef} />
          </div>

          <form className="ai-foot" onSubmit={send}>
            <input value={input} onChange={(e) => setInput(e.target.value)}
              placeholder={t('ai.placeholder')} maxLength={2000} disabled={busy} />
            <button className="primary" type="submit" disabled={busy || !input.trim()}>
              <Icon n="send" />{t('ai.send')}
            </button>
          </form>
        </div>
      )}
    </>
  );
}
